import axios from 'axios';

/**
 * Custom Error class to provide detailed API error information
 */
export class ApiError extends Error {
  constructor(message, status, data) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

/**
 * Singleton Axios instance for API requests
 * Implements interceptors for auth injection and global error handling
 */
class ApiClient {
  constructor() {
    // Fail safe fallback validation
    const baseURL = import.meta.env.VITE_API_BASE_URL;
    if (!baseURL) {
      console.warn('VITE_API_BASE_URL is not defined used default');
    }

    this.instance = axios.create({
      baseURL: baseURL || '/api',
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true,
      timeout: 900000, // 10 minutes to allow large uploads
      maxContentLength: Infinity,
      maxBodyLength: Infinity,
    });

    this.setupInterceptors();
  }

  setupInterceptors() {
    // Request interceptor: Inject Auth Token
    this.instance.interceptors.request.use(
      (config) => {
        // Optimized: Use the token from defaults if set (via setAuthToken)
        // Fallback: Check localStorage if header is missing (e.g. early init)
        if (!config.headers.Authorization) {
          const token = localStorage.getItem('token');
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        }

        // Add device signature header for device tracking
        const deviceSignature = localStorage.getItem('pm_device_signature');
        if (deviceSignature) {
          config.headers['X-Device-Signature'] = deviceSignature;
        }

        // Handle FormData automatically
        if (config.data instanceof FormData) {
          delete config.headers['Content-Type'];
        }

        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor: Global Error Handling
    this.instance.interceptors.response.use(
      (response) => {
        // Return raw data usually, but sometimes full response is needed. 
        // Architecture decision: Keep returning response.data to match previous contract.
        return response;
      },
      (error) => {
        // Handle 401 Unauthorized globally
        if (error.response?.status === 401) {
          // Dispatch a custom event that AuthContext can listen to
          // This avoids circular dependencies between API and AuthContext
          window.dispatchEvent(new Event('auth:unauthorized'));
        }

        if (error.response) {
          // Server responded with a status code outside the 2xx range
          const message = error.response.data?.message || 
                         error.response.data?.error || 
                         error.message || 
                         'An external API error occurred';
          
          return Promise.reject(new ApiError(message, error.response.status, error.response.data));
        } else if (error.request) {
          // Request was made but no response received
          return Promise.reject(new ApiError('Network error. Please check your connection.', 0, null));
        } else {
          // Something happened in setting up the request
          return Promise.reject(new ApiError(error.message || 'An unexpected client error occurred', 0, null));
        }
      }
    );
  }

  /**
   * Generic GET request
   * @param {string} url - Endpoint URL
   * @param {object} config - Axios config
   */
  async get(url, config = {}) {
    const response = await this.instance.get(url, config);
    return response.data;
  }

  /**
   * Generic POST request
   * @param {string} url - Endpoint URL
   * @param {any} data - Request body
   * @param {object} config - Axios config
   */
  async post(url, data = {}, config = {}) {
    const response = await this.instance.post(url, data, config);
    return response.data;
  }

  /**
   * Generic PUT request
   * @param {string} url - Endpoint URL
   * @param {any} data - Request body
   * @param {object} config - Axios config
   */
  async put(url, data = {}, config = {}) {
    const response = await this.instance.put(url, data, config);
    return response.data;
  }

  /**
   * Generic PATCH request
   * @param {string} url - Endpoint URL
   * @param {any} data - Request body
   * @param {object} config - Axios config
   */
  async patch(url, data = {}, config = {}) {
    const response = await this.instance.patch(url, data, config);
    return response.data;
  }

  /**
   * Generic DELETE request
   * @param {string} url - Endpoint URL
   * @param {object} config - Axios config
   */
  async delete(url, config = {}) {
    const response = await this.instance.delete(url, config);
    return response.data;
  }

  /**
   * Set or Clear Authentication Token
   * @param {string|null} token - JWT Token or null to clear
   */
  setAuthToken(token) {
    if (token) {
      this.instance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      // Note: We don't set localStorage here anymore to maintain separation of concerns.
      // AuthContext handles storage. This method just syncs the API instance.
    } else {
      delete this.instance.defaults.headers.common['Authorization'];
    }
  }

  /**
   * Expose the raw axios instance if needed
   */
  getInstance() {
    return this.instance;
  }
}

// Create and export singleton instance
const api = new ApiClient();

export default api;

